import { EnvironmentStrip } from './EnvironmentStrip';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { apiHandlers } from '../api/client';
import { useI18n } from '../i18n';
import { ErrorState, PageLoader, EmptyState } from '../components/Feedback';
import { BootFallback, wasPrerendered } from '../prerender/boot';
import { ButtonLink } from '../components/Button';
import { useSession, useClearSession } from './session';
import { Shell } from './Shell';
import { QuarterProvider } from './quarter';
import { LegacyRedirect } from './legacyRoutes';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { TranslationNotice } from './TranslationNotice';
import { isPrelaunchPath, usePrelaunch } from './prelaunch';

const HomePage = lazy(() => import('../features/home/HomePage'));
const QuestionsPage = lazy(() => import('../features/questions/QuestionsPage'));
const HistoryPage = lazy(() => import('../features/history/HistoryPage'));
const SubmitPage = lazy(() => import('../features/submit/SubmitPage'));
const InvoiceEditorPage = lazy(() => import('../features/invoicing/InvoiceEditorPage'));
const DocumentsPage = lazy(() => import('../features/documents/DocumentsPage'));
const TransactionsPage = lazy(() => import('../features/transactions/TransactionsPage'));
const SettingsPage = lazy(() => import('../features/settings/SettingsPage'));
const OnboardingPage = lazy(() => import('../features/onboarding/OnboardingPage'));
const KorPage = lazy(() => import('../features/kor/KorPage'));
const LoginPage = lazy(() => import('../features/auth/LoginPage'));
const InvitePage = lazy(() => import('../features/auth/InvitePage'));
const MagicLinkPage = lazy(() => import('../features/auth/MagicLinkPage'));
const VerifyEmailPage = lazy(() => import('../features/auth/VerifyEmailPage'));
const TermsGatePage = lazy(() => import('../features/auth/TermsGatePage'));
const OpsPage = lazy(() => import('../features/ops/OpsPage'));
const LandingPage = lazy(() => import('../features/landing/LandingPage'));
const PrivacyPage = lazy(() => import('../features/public/PrivacyPage'));
const TermsPage = lazy(() => import('../features/public/TermsPage'));
const SubprocessorsPage = lazy(() => import('../features/public/SubprocessorsPage'));
const DpaPage = lazy(() => import('../features/public/DpaPage'));
const SiteHome = lazy(() => import('../features/site/pages/HomePage'));
const SiteProduct = lazy(() => import('../features/site/pages/ProductPage'));
const SiteHow = lazy(() => import('../features/site/pages/HowItWorksPage'));
const SiteForWhom = lazy(() => import('../features/site/pages/ForWhomPage'));
const SitePricing = lazy(() => import('../features/site/pages/PricingPage'));
const SiteFaq = lazy(() => import('../features/site/pages/FaqPage'));
const SiteExample = lazy(() => import('../features/site/pages/ExamplePage'));
const SiteTrust = lazy(() => import('../features/site/pages/TrustPage'));
const SiteAbout = lazy(() => import('../features/site/pages/AboutPage'));
const SiteContact = lazy(() => import('../features/site/pages/ContactPage'));
const SiteReceived = lazy(() => import('../features/site/pages/RequestReceivedPage'));
const SiteCheck = lazy(() => import('../features/site/pages/FitCheckPage'));
const SiteForgot = lazy(() => import('../features/site/pages/ForgotPage'));
const SiteHelp = lazy(() => import('../features/site/pages/HelpArticlePage'));

const PUBLIC_PREFIXES = ['/login', '/register', '/invite'];

/** Any 401 from the API sends the user to /login and remembers where they were. */
function useUnauthenticatedRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const clear = useClearSession();
  useEffect(() => {
    apiHandlers.onUnauthenticated = (code) => {
      if (PUBLIC_PREFIXES.some((p) => window.location.pathname.startsWith(p))) return;
      clear();
      const next = window.location.pathname + window.location.search;
      navigate(`/login?${code === 'totp_required' ? 'step=totp&' : ''}next=${encodeURIComponent(next)}`, { replace: true });
    };
    return () => {
      apiHandlers.onUnauthenticated = null;
    };
  }, [navigate, clear, location.pathname]);
}

/**
 * Logged-in pages. Logged out, `/` shows the public landing page (2026-10 redesign); every other page goes to /login
 * and comes back afterwards.
 */
function RequireAuth({ children }: { children: JSX.Element }) {
  const { me, loading, error, totpPending } = useSession();
  const location = useLocation();
  const isHome = location.pathname === '/';
  // A prerendered home page (visitor without a session cookie) stays visible while the session is checked.
  if (loading) return isHome && wasPrerendered('/') ? <BootFallback /> : <PageLoader />;
  if (totpPending) return <Navigate to={`/login?step=totp&next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (error && !me && !isHome)
    return (
      <div className="main">
        <ErrorState error={error} onRetry={() => window.location.reload()} />
      </div>
    );
  if (!me) return isHome ? <SiteHome /> : <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  // P0-16: new terms since the last acceptance → ask before anything else.
  if (me.termsRequired) return <TermsGatePage />;
  return children;
}

function RequireStaff({ children }: { children: JSX.Element }) {
  const { user } = useSession();
  if (!user?.staffRole) return <Navigate to="/" replace />;
  return children;
}

function NotFound() {
  const { t } = useI18n();
  return (
    <div className="card">
      <EmptyState
        title={t('notFound.title')}
        text={t('notFound.text')}
        action={
          <ButtonLink to="/" variant="primary">
            {t('notFound.action')}
          </ButtonLink>
        }
      />
    </div>
  );
}

/**
 * PRELAUNCH (btwkwartier.nl before the beta): only the landing page with the waitlist, /privacy, /voorwaarden, /subverwerkers and /verwerkersovereenkomst.
 * Every other address (login, register, the app) goes to /. /beheer never reaches the SPA: Caddy sends it to the API.
 */
export function PrelaunchRoutes() {
  const { pathname } = useLocation();
  if (!isPrelaunchPath(pathname)) return <Navigate to="/" replace />;
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/voorwaarden" element={<TermsPage />} />
      <Route path="/subverwerkers" element={<SubprocessorsPage />} />
      <Route path="/verwerkersovereenkomst" element={<DpaPage />} />
    </Routes>
  );
}

export function App() {
  useUnauthenticatedRedirect();
  const { pathname } = useLocation();
  const pre = usePrelaunch();
  if (pre.loading) return <BootFallback />;
  if (pre.prelaunch)
    return (
      <ErrorBoundary resetKey={pathname}>
        <Suspense fallback={<PageLoader />}>
          <PrelaunchRoutes />
        </Suspense>
      </ErrorBoundary>
    );
  return (
    // DC-01 / AR-13: a crash (or a stale chunk after a deploy) never blanks the whole app; resets on navigation.
    <ErrorBoundary resetKey={pathname}>
    <EnvironmentStrip />
    <TranslationNotice />
    <Suspense fallback={<BootFallback />}>
      <Routes>
        {/* English website (SEO 2026-10-27): the same pages under /en; the Dutch ones below stay at the root. */}
        <Route path="/en" element={<SiteHome />} />
        <Route path="/en/product" element={<SiteProduct />} />
        <Route path="/en/zo-werkt-het" element={<SiteHow />} />
        <Route path="/en/voor-wie" element={<SiteForWhom />} />
        <Route path="/en/prijzen" element={<SitePricing />} />
        <Route path="/en/veelgestelde-vragen" element={<SiteFaq />} />
        <Route path="/en/voorbeeld" element={<SiteExample />} />
        <Route path="/en/vertrouwen" element={<SiteTrust />} />
        <Route path="/en/over-ons" element={<SiteAbout />} />
        <Route path="/en/contact" element={<SiteContact />} />
        <Route path="/en/aanvraag-ontvangen" element={<SiteReceived />} />
        <Route path="/en/btw-check" element={<SiteCheck />} />
        <Route path="/en/hulp/:slug" element={<SiteHelp />} />
        <Route path="/en/privacy" element={<PrivacyPage />} />
        <Route path="/en/voorwaarden" element={<TermsPage />} />
        <Route path="/en/subverwerkers" element={<SubprocessorsPage />} />
        <Route path="/en/verwerkersovereenkomst" element={<DpaPage />} />
        <Route path="/product" element={<SiteProduct />} />
        <Route path="/zo-werkt-het" element={<SiteHow />} />
        <Route path="/voor-wie" element={<SiteForWhom />} />
        <Route path="/prijzen" element={<SitePricing />} />
        <Route path="/veelgestelde-vragen" element={<SiteFaq />} />
        <Route path="/voorbeeld" element={<SiteExample />} />
        <Route path="/vertrouwen" element={<SiteTrust />} />
        <Route path="/over-ons" element={<SiteAbout />} />
        <Route path="/contact" element={<SiteContact />} />
        <Route path="/aanvraag-ontvangen" element={<SiteReceived />} />
        <Route path="/btw-check" element={<SiteCheck />} />
        <Route path="/login/wachtwoord-vergeten" element={<SiteForgot />} />
        <Route path="/hulp/:slug" element={<SiteHelp />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<LoginPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/voorwaarden" element={<TermsPage />} />
        <Route path="/subverwerkers" element={<SubprocessorsPage />} />
        <Route path="/verwerkersovereenkomst" element={<DpaPage />} />
        {/* Emailed links carry their token in the fragment (#token) so it never reaches a server log (AR-05). */}
        <Route path="/login/link" element={<MagicLinkPage />} />
        <Route path="/login/verify-email" element={<VerifyEmailPage />} />
        <Route path="/invite" element={<InvitePage />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route
          path="/onboarding"
          element={
            <RequireAuth>
              {/* The onboarding uses the filing quarter (partner-accountant referral, DC-01). */}
              <QuarterProvider>
                <OnboardingPage />
              </QuarterProvider>
            </RequireAuth>
          }
        />
        <Route
          element={
            <RequireAuth>
              <Shell />
            </RequireAuth>
          }
        >
          {/* Main sections (fixed, 2026-10): Dit kwartaal · Documenten · Historie. Phase 2 replaces the page bodies. */}
          <Route index element={<HomePage />} />
          <Route path="documenten" element={<DocumentsPage />} />
          <Route path="documenten/transacties" element={<TransactionsPage />} />
          <Route path="documenten/facturen/:id" element={<InvoiceEditorPage />} />
          <Route path="historie" element={<HistoryPage />} />
          {/* Flows inside "Dit kwartaal" */}
          <Route path="vragen" element={<QuestionsPage />} />
          {/* KOR company (2026-10-30): no returns; the year's turnover against the € 20,000 limit. */}
          <Route path="kor/:entityId" element={<KorPage />} />
          <Route path="aangiftes" element={<RedirectKeepSearch to="/historie" />} />
          {/* Step 3 of a quarter: "Controleren en indienen" (2026-10, replaces the 6-step workplace). */}
          <Route path="aangiftes/:entityId/:quarter" element={<SubmitPage />} />
          {/* Old addresses (phase 2): transactions live under 资料, companies and the log under settings. */}
          <Route path="transacties" element={<LegacyRedirect />} />
          <Route path="bedrijven" element={<LegacyRedirect />} />
          <Route path="bedrijven/:id" element={<LegacyRedirect />} />
          <Route path="bedrijven/:id/:quarter" element={<LegacyRedirect />} />
          <Route path="logboek" element={<LegacyRedirect />} />
          <Route path="instellingen" element={<Navigate to="/instellingen/profiel" replace />} />
          <Route path="instellingen/:tab" element={<SettingsPage />} />
          <Route path="instellingen/bedrijven/:id" element={<SettingsPage tab="bedrijven" />} />
          <Route
            path="ops/*"
            element={
              <RequireStaff>
                <OpsPage />
              </RequireStaff>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

/** Old address → new one, keeping filters such as ?status=draft_ready. */
function RedirectKeepSearch({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

